/**
 * One tool definition: edit it, test it, switch it on and off. Reached by clicking a row
 * on /tools — the list is for finding, this page is for changing, the same
 * split as Agents/AgentEdit.
 *
 * WRITE-ONLY HEADERS, restated because this page re-implements the form: the
 * server never returns header VALUES, only names. An untouched editor omits
 * `headers` from the PATCH (stored values survive); any touch sends the full
 * replacement map; clearing needs an explicit confirmation tick.
 *
 * ACTIVE/INACTIVE IS THE OFF-SWITCH, and it is an ordinary, reversible edit:
 * inactive tools keep their agent references and their name, and hydration
 * skips them until they are switched back. Revocation (the permanent,
 * name-burning end of life) still exists in the API, and a revoked tool's page
 * stays reachable read-only so the record can be read but not rewritten.
 */
import { useEffect, useState } from 'react'
import { ApiError, toolDefs, type PatchToolDefBody, type ToolDefView, type ToolTestResult } from '@/api'
import { can } from '@/lib/auth'
import { hostOf, parseParams, skeletonFor, toolMessageFor } from '@/lib/tool-form'
import { useAuth } from '@/components/auth-provider'
import { Link } from '@/components/app-sidebar'
import { fmtWhen } from '@/components/state-pill'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  HeadersEditor,
  buildHeaders,
  headerRowsClean,
  headerRowsDirty,
  seedHeaderRows,
  type HeaderRow,
} from '@/components/headers-editor'

interface Draft {
  name: string
  description: string
  displayName: string
  displayDescription: string
  url: string
  includeTranscript: boolean
  sendClientPayload: boolean
  enabled: boolean
  paramsText: string
  headerRows: HeaderRow[]
  clearConfirmed: boolean
}

function draftFrom(row: ToolDefView): Draft {
  return {
    name: row.name,
    description: row.description,
    // '' in the draft means unset; the save maps it back to null (clear).
    displayName: row.displayName ?? '',
    displayDescription: row.displayDescription ?? '',
    url: row.url,
    includeTranscript: row.includeTranscript,
    sendClientPayload: row.sendClientPayload,
    enabled: row.enabled,
    // Pretty-printed so a hand-written schema comes back readable. The dirty
    // check compares PARSED values, so reformatting alone is not a change.
    paramsText: JSON.stringify(row.parameters, null, 2),
    headerRows: seedHeaderRows(row.headerNames),
    clearConfirmed: false,
  }
}

export function ToolEditPage({ id }: { id: string }) {
  const { user } = useAuth()
  const [server, setServer] = useState<ToolDefView | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    toolDefs
      .get(id, ac.signal)
      .then((row) => {
        setServer(row)
        setDraft(draftFrom(row))
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setLoadError(toolMessageFor(e))
      })
    return () => ac.abort()
  }, [id])

  // ------------------------------------------------------------------- test
  const [testArgs, setTestArgs] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<ToolTestResult | null>(null)
  const [testRunning, setTestRunning] = useState(false)
  const [testError, setTestError] = useState<string | null>(null)

  const runTest = () => {
    if (!server || testRunning) return
    // `testArgs` is null until the FIRST keystroke in the box — the textarea
    // shows the schema skeleton as its value in that state, so the skeleton is
    // what an untouched box means. Refusing on null here made the button a
    // silent no-op for any tool tested without editing the args, which for a
    // no-argument tool was EVERY test (there is nothing to type).
    const text = testArgs ?? skeletonFor(server.parameters)
    let args: unknown
    try {
      args = JSON.parse(text)
    } catch {
      setTestError('The arguments are not valid JSON.')
      return
    }
    if (typeof args !== 'object' || args === null || Array.isArray(args)) {
      setTestError('The arguments must be a JSON object, like the model would send.')
      return
    }
    setTestRunning(true)
    setTestError(null)
    setTestResult(null)
    toolDefs
      .test(server.id, args as Record<string, unknown>, AbortSignal.timeout(25_000))
      .then((res) => {
        setTestRunning(false)
        setTestResult(res)
      })
      .catch((e: unknown) => {
        setTestRunning(false)
        setTestError(toolMessageFor(e))
      })
  }

  if (loadError) {
    return (
      <div className="min-w-0 max-w-4xl">
        <p className="err">{loadError}</p>
        <p className="mt-2 text-sm">
          <Link to="/tools" className="underline underline-offset-4">
            Back to the tools list
          </Link>
        </p>
      </div>
    )
  }
  if (!server || !draft) return <p className="loading">Loading…</p>

  const revoked = server.revokedAt !== null
  const readOnly = !can(user, 'write') || revoked

  const onSave = () => {
    if (saving || readOnly) return
    const params = parseParams(draft.paramsText)
    if ('err' in params) {
      setSaveError(params.err)
      return
    }
    const headersTouched = headerRowsDirty(draft.headerRows, server.headerNames)
    if (headersTouched && !headerRowsClean(draft.headerRows, true)) {
      setSaveError('Fix the header rows first — see the messages beside them.')
      return
    }
    const clearing = headersTouched && draft.headerRows.length === 0 && server.headerNames.length > 0
    if (clearing && !draft.clearConfirmed) {
      setSaveError('Confirm clearing the stored headers first — see the note in the headers editor.')
      return
    }

    // Only what changed, PATCH-style. `parameters` compared by re-serialising
    // both sides the same way, so pretty-printing alone is not an edit.
    const body: PatchToolDefBody = {}
    if (draft.name.trim() !== server.name) body.name = draft.name.trim()
    if (draft.description.trim() !== server.description) body.description = draft.description.trim()
    // '' in the draft is "unset" — sent as null so the server clears the column.
    const dn = draft.displayName.trim()
    if (dn !== (server.displayName ?? '')) body.displayName = dn === '' ? null : dn
    const dd = draft.displayDescription.trim()
    if (dd !== (server.displayDescription ?? '')) body.displayDescription = dd === '' ? null : dd
    if (draft.url.trim() !== server.url) body.url = draft.url.trim()
    if (draft.includeTranscript !== server.includeTranscript) body.includeTranscript = draft.includeTranscript
    if (draft.sendClientPayload !== server.sendClientPayload) body.sendClientPayload = draft.sendClientPayload
    if (draft.enabled !== server.enabled) body.enabled = draft.enabled
    if (JSON.stringify(params.ok) !== JSON.stringify(server.parameters)) body.parameters = params.ok
    // WRITE-ONLY SEMANTICS: untouched editor omits `headers` (stored values
    // survive), any touch sends the full map, a confirmed clear sends {}.
    if (headersTouched) body.headers = buildHeaders(draft.headerRows)

    if (Object.keys(body).length === 0) {
      setSaveError('Nothing changed.')
      return
    }
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    // NO AbortController tied to this component — a write must survive
    // navigation, same rule as every save in this app. A timeout bounds it.
    toolDefs
      .update(server.id, body, AbortSignal.timeout(20_000))
      .then((row) => {
        setSaving(false)
        setSaved(true)
        setServer(row)
        setDraft(draftFrom(row))
      })
      .catch((e: unknown) => {
        setSaving(false)
        setSaveError(toolMessageFor(e))
      })
  }

  return (
    <div className="min-w-0 max-w-4xl">
      {/* No "Test this tool" shortcut up here any more — it only scrolled to the
          test panel below, which is one flick of the wheel away and has its own
          Run button (removed 2026-08-23, with its webhook-page twin). */}
      <div className="mb-4">
        <h1 className="text-xl font-semibold tracking-tight">
          <span className="mono">{server.name}</span>
          {revoked && <span className="pill pill-plain ml-2 align-middle">revoked</span>}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          <span className="mono">{server.id}</span> &middot; created {fmtWhen(server.createdAt)}
          {revoked && server.revokedAt ? <> &middot; revoked {fmtWhen(server.revokedAt)}</> : null}
        </p>
      </div>

      {revoked && (
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Revoked: new sessions no longer receive this tool and the name is burned. The
          definition stays readable because past calls are recorded under it.
        </p>
      )}
      {!revoked && !can(user, 'write') && (
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Read-only: editing a tool is a write, and this account is a{' '}
          <span className="mono">{user?.role}</span>.
        </p>
      )}

      <section className="panel">
        <h2>
          Definition
          <span className="note">past calls stay recorded under the name they were made with</span>
        </h2>
        <div className="body flex flex-col gap-4">
          {/* The reversible off-switch, first — the one control that answers
              "is this tool doing anything right now". Not revocation: flipping
              it back needs no ceremony, and attached agents keep the reference
              throughout. */}
          <div className="flex items-center gap-3">
            <Switch
              id="enabled"
              checked={draft.enabled}
              onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
              disabled={readOnly}
            />
            <Label htmlFor="enabled" className="cursor-pointer">
              {draft.enabled ? 'Active' : 'Inactive'}
            </Label>
            <span className="text-xs text-muted-foreground">
              {draft.enabled
                ? 'offered to every agent that lists it, from their next session'
                : 'agents keep the reference, but new sessions skip this tool until it is active again'}
            </span>
          </div>

          <div className="grid2">
            <div className="field">
              <Label htmlFor="toolName">Name</Label>
              <Input
                id="toolName"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="mono"
                autoCapitalize="none"
                spellCheck={false}
                disabled={readOnly}
              />
              <p className="text-xs text-muted-foreground">
                The name the model calls. Renaming takes effect for NEW sessions; running
                sessions keep their snapshot, past calls stay recorded under the old name,
                and the old name becomes free for a future tool.
              </p>
            </div>
            <div className="field">
              <Label htmlFor="url">Endpoint URL</Label>
              <Input
                id="url"
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                className="mono"
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="grid2">
            <div className="field">
              <Label htmlFor="toolDisplayName">Display name</Label>
              <Input
                id="toolDisplayName"
                value={draft.displayName}
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                placeholder="Order lookup"
                maxLength={120}
                disabled={readOnly}
              />
              <p className="text-xs text-muted-foreground">
                Optional. What end users see on the widget&rsquo;s tool card instead of the
                raw name. Informational only — the model never reads it. Clear it to fall
                back to the auto-title.
              </p>
            </div>
            <div className="field">
              <Label htmlFor="toolDisplayDescription">Display description</Label>
              <Input
                id="toolDisplayDescription"
                value={draft.displayDescription}
                onChange={(e) => setDraft({ ...draft, displayDescription: e.target.value })}
                placeholder="Checks an order's status"
                maxLength={300}
                disabled={readOnly}
              />
              <p className="text-xs text-muted-foreground">
                Optional. A short line under the display name, same audience: end users,
                never the model.
              </p>
            </div>
          </div>

          {/* Labelled fields so the row's controls share a baseline — a bare
              checkbox floating at the top of a cell is what made this row look
              broken once already. */}
          <div className="grid2">
            <div className="field">
              <Label htmlFor="includeTranscript">Transcript</Label>
              <label
                htmlFor="includeTranscript"
                className="flex h-9 cursor-pointer items-center gap-2 text-sm"
              >
                <Checkbox
                  id="includeTranscript"
                  checked={draft.includeTranscript}
                  onCheckedChange={(v) => setDraft({ ...draft, includeTranscript: v === true })}
                  disabled={readOnly}
                />
                Send the transcript with each call
              </label>
            </div>
            <div className="field">
              <Label htmlFor="sendClientPayload">Client payload</Label>
              <label
                htmlFor="sendClientPayload"
                className="flex h-9 cursor-pointer items-center gap-2 text-sm"
              >
                <Checkbox
                  id="sendClientPayload"
                  checked={draft.sendClientPayload}
                  onCheckedChange={(v) => setDraft({ ...draft, sendClientPayload: v === true })}
                  disabled={readOnly}
                />
                Send the client params with each call
              </label>
              <p className="text-xs text-muted-foreground">
                The session&rsquo;s client data params ride as{' '}
                <span className="mono">params</span> in every POST. Unticked, the body
                carries no <span className="mono">params</span> key.
              </p>
            </div>
          </div>
          <div className="field">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              rows={2}
              disabled={readOnly}
            />
            <p className="text-xs text-muted-foreground">
              The model reads this to decide WHEN to call the tool — write it for the model,
              not for this page.
            </p>
          </div>
          <div className="field">
            <Label htmlFor="params">Parameters — JSON Schema</Label>
            <Textarea
              id="params"
              value={draft.paramsText}
              onChange={(e) => setDraft({ ...draft, paramsText: e.target.value })}
              rows={8}
              className="font-mono text-xs"
              spellCheck={false}
              disabled={readOnly}
            />
          </div>

          {!readOnly && (
            <HeadersEditor
              idPrefix="toolEdit"
              rows={draft.headerRows}
              onChange={(update) =>
                setDraft((prev) => (prev ? { ...prev, headerRows: update(prev.headerRows) } : prev))
              }
              storedNames={server.headerNames}
              clearConfirmed={draft.clearConfirmed}
              onClearConfirmed={(on) => setDraft({ ...draft, clearConfirmed: on })}
            />
          )}

          {!readOnly && (
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={onSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDraft(draftFrom(server))
                  setSaveError(null)
                  setSaved(false)
                }}
                disabled={saving}
              >
                Discard
              </Button>
              <span className="text-xs text-muted-foreground">
                {saved
                  ? 'Saved. Sessions already running keep the definition they started with; the next session gets this one.'
                  : 'Sessions already running keep the definition they started with; the next session gets this one.'}
              </span>
              {saveError && <p className="err w-full text-sm">{saveError}</p>}
            </div>
          )}
        </div>
      </section>

      {!readOnly && (
        <section className="panel">
          <h2>
            Test
            <span className="note">
              one real POST to {hostOf(draft.url)}, fired from the panel with the stored
              headers — the browser never holds them
            </span>
          </h2>
          <div className="body flex flex-col gap-3">
            <div className="field">
              <Label htmlFor="test-args">Arguments</Label>
              <Textarea
                id="test-args"
                value={testArgs ?? skeletonFor(server.parameters)}
                onChange={(e) => setTestArgs(e.target.value)}
                rows={6}
                className="max-w-2xl font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                The JSON object the model would produce from the schema above — keys
                pre-filled from it. The endpoint also receives a{' '}
                <span className="mono">panel-test-…</span> session id, so it can tell a
                rehearsal from a live call. The test runs against the SAVED definition;
                unsaved edits above are not what fires.
              </p>
            </div>

            <div>
              <Button size="sm" onClick={runTest} disabled={testRunning}>
                {testRunning ? 'Calling…' : 'Send test call'}
              </Button>
            </div>

            {testError && <p className="err text-sm">{testError}</p>}

            {testResult && (
              <div className="flex flex-col gap-2">
                <p className="text-sm">
                  <span className={testResult.ok ? 'pill' : 'pill pill-plain text-ev-bad'}>
                    {testResult.status === null ? 'no response' : `HTTP ${testResult.status}`}
                  </span>{' '}
                  <span className="text-muted-foreground">
                    in {testResult.durationMs.toLocaleString('en-GB')} ms
                    {testResult.ok
                      ? ' — this is what the model would be handed.'
                      : ' — a live call would surface this to the model as a tool failure.'}
                  </span>
                </p>
                {testResult.error && <p className="err text-sm">{testResult.error}</p>}
                {testResult.body !== null && (
                  <pre className="max-h-80 max-w-3xl overflow-auto rounded-md border bg-muted/50 p-3 text-xs">
                    {JSON.stringify(testResult.body, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        </section>
      )}

    </div>
  )
}
